import { MATTER_SEARCH_SORT_FIELDS } from "@/lib/utils/matters/matter-search-sort";
import { keepPreviousSearchData } from "@/lib/data/react-query/search/scope";
import { INCIDENT_TYPE_COLUMN_ALIASES } from "@/lib/utils/matters/incident-classification";
import { migrateTableColumnPrefs } from "@/lib/utils/tanstack-table-local-storage";
import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { MatterRowContextMenu } from "@/components/features/matters/MatterRowContextMenu";
import {
  Card,
  CardHeader,
  DataTable,
  DraggableCardDataTable,
  type DataTableChrome,
  Typography,
} from "@/components/ui";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import { searchMattersQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import {
  getSearchMattersCount,
  getSearchMattersNextPageNumber,
  type MatterSearchResult,
  type SearchMattersInput,
} from "@/lib/data/requests";
import { type tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { ensureClientLabelFilter } from "@/lib/utils/matters/matter-filter-utils";
import { readTanstackTablePrefs } from "@/lib/utils/tanstack-table-local-storage";
import { useQuery } from "@tanstack/react-query";
import {
  type PaginationState,
  type Row,
  type SortingState,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { getMattersTableColumns } from "./matters-table-columns";

export interface MattersTableProps {
  showRelatedMatters?: boolean;
  /** When `matterDashboard`, matches My Matters list card chrome (full matters page only). */
  chrome?: DataTableChrome;
  entityLabelPlural?: string;
  defaultPageSize?: number;
  searchBar?: React.ReactNode;
  toolbarFilters?: React.ReactNode;
  /** Matter dashboard chrome only: actions before the Columns control. */
  tableToolbarBeforeColumns?: React.ReactNode;
  tableButtons?: React.ReactNode;
  /** General text search passed to the matter search endpoint. */
  generalSearch?: string;
  massTortId?: string;
  searchObject: { [key: string]: string[] };
  forcedSearchObject?: { [key: string]: string[] };
  onRowClick: (row: Row<MatterSearchResult>) => void;
  tableName: (typeof tanstackTableNames)[keyof typeof tanstackTableNames];
  excludeMatterIds?: string[];
  filterMatters?: (matter: MatterSearchResult) => boolean;
  showReferredByColumn?: boolean;
  showReferredToColumn?: boolean;
  showDroppedDateColumn?: boolean;
  statusCategories?: SearchMattersInput["statusCategories"];
  renderContextMenu?: (
    row: Row<MatterSearchResult>,
    trigger: React.ReactNode,
  ) => React.ReactNode;
  onRowCountChange?: (count: number | undefined) => void;
  /** Constrains the table body to its parent and enables internal vertical scrolling. */
  scrollableBody?: boolean;
}

const NEW_CLASSIFICATION_COLUMN_ANCHORS = {
  "Claim Types": "Incident Type",
};
const DEFAULT_MATTERS_SORTING: SortingState = [
  { id: "Date Created", desc: true },
];

export const MattersTable = ({
  chrome = "default",
  entityLabelPlural = "matters",
  defaultPageSize = 10,
  searchBar,
  toolbarFilters,
  tableToolbarBeforeColumns,
  tableButtons,
  generalSearch = "",
  massTortId,
  searchObject,
  forcedSearchObject,
  onRowClick,
  tableName,
  filterMatters,
  excludeMatterIds,
  showReferredByColumn = false,
  showReferredToColumn = false,
  showDroppedDateColumn = false,
  statusCategories = [...ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS],
  renderContextMenu,
  onRowCountChange,
  scrollableBody = false,
  showRelatedMatters = false,
}: MattersTableProps) => {
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: 0,
    pageSize:
      readTanstackTablePrefs(tableName).pagination?.pageSize ?? defaultPageSize,
  }));
  const [sorting, setSorting] = useState<SortingState>(() => {
    const savedSorting = migrateTableColumnPrefs(
      readTanstackTablePrefs(tableName),
      INCIDENT_TYPE_COLUMN_ALIASES,
    ).sorting;

    return savedSorting ?? DEFAULT_MATTERS_SORTING;
  });
  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const statusLabelsById = useMemo(
    () =>
      new Map(
        (matterSettingsData?.data.statuses ?? []).map((status) => [
          status.id,
          status.label,
        ]),
      ),
    [matterSettingsData?.data.statuses],
  );
  const typeLabelsById = useMemo(
    () =>
      new Map(
        (matterSettingsData?.data.incidentTypes ?? []).map((type) => [
          type.id,
          getIncidentTypeLabel(type),
        ]),
      ),
    [matterSettingsData?.data.incidentTypes],
  );
  const mattersTableColumns = useMemo(() => {
    const columns = getMattersTableColumns({
      includeReferredBy: showReferredByColumn,
      includeReferredTo: showReferredToColumn,
      includeDroppedDate: showDroppedDateColumn,
      statusLabelsById,
      typeLabelsById,
      showRelatedMatters,
    });

    return columns;
  }, [
    showReferredByColumn,
    showReferredToColumn,
    showDroppedDateColumn,
    statusLabelsById,
    typeLabelsById,
    showRelatedMatters,
  ]);

  const mattersTableColumnsPlainHeaders = useMemo(() => {
    const columns = getMattersTableColumns({
      columnHeaders: "plain",
      includeReferredBy: showReferredByColumn,
      includeReferredTo: showReferredToColumn,
      includeDroppedDate: showDroppedDateColumn,
      statusLabelsById,
      typeLabelsById,
      showRelatedMatters,
    });

    return columns;
  }, [
    showReferredByColumn,
    showReferredToColumn,
    showDroppedDateColumn,
    statusLabelsById,
    typeLabelsById,
    showRelatedMatters,
  ]);

  const augmentedSearchObject = useMemo(
    () => ({ ...ensureClientLabelFilter(searchObject), ...forcedSearchObject }),
    [forcedSearchObject, searchObject],
  );
  const newColumnAnchors = useMemo(
    () =>
      showDroppedDateColumn
        ? {
            ...NEW_CLASSIFICATION_COLUMN_ANCHORS,
            "Dropped Date": "Status",
          }
        : NEW_CLASSIFICATION_COLUMN_ANCHORS,
    [showDroppedDateColumn],
  );
  const searchSignature = useMemo(
    () =>
      JSON.stringify({
        augmentedSearchObject,
        generalSearch: generalSearch.trim(),
        massTortId,
        excludeMatterIds,
        statusCategories,
        showRelatedMatters,
      }),
    [
      augmentedSearchObject,
      generalSearch,
      massTortId,
      statusCategories,
      showRelatedMatters,
      excludeMatterIds,
    ],
  );
  const searchInput = useMemo<SearchMattersInput>(() => {
    const { unassigned, ...filters } = augmentedSearchObject;

    return {
      ...filters,
      massTortId,
      excludeMatterIds,
      includeRelatedMatters: showRelatedMatters || undefined,
      search: generalSearch.trim() || undefined,
      unassigned: unassigned?.includes("true"),
      statusCategories,
    };
  }, [
    augmentedSearchObject,
    generalSearch,
    massTortId,
    statusCategories,
    showRelatedMatters,
    excludeMatterIds,
  ]);
  const sortInput = useMemo(() => {
    const activeSort = sorting.at(0);
    const sort = activeSort && MATTER_SEARCH_SORT_FIELDS[activeSort.id];

    if (!sort) {
      return {};
    }

    return {
      order: activeSort.desc ? ("desc" as const) : ("asc" as const),
      sort,
    };
  }, [sorting]);

  useEffect(() => {
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [searchSignature]);

  const {
    data: mattersData,
    isLoading: isMattersLoading,
    isFetching: isMattersFetching,
    isError: isMattersError,
  } = useQuery({
    ...searchMattersQueryOptions({
      ...searchInput,
      ...sortInput,
      pageNumber: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    placeholderData: keepPreviousSearchData,
  });
  const nextPageNumber = getSearchMattersNextPageNumber(mattersData);
  const stableRowCount = getSearchMattersCount(mattersData);

  useEffect(() => {
    onRowCountChange?.(stableRowCount);
  }, [onRowCountChange, stableRowCount]);
  const pageCount =
    typeof stableRowCount === "number"
      ? Math.max(1, Math.ceil(stableRowCount / pagination.pageSize))
      : Math.max(
          1,
          nextPageNumber ? pagination.pageIndex + 2 : pagination.pageIndex + 1,
        );

  const matters = useMemo(() => mattersData?.data ?? [], [mattersData]);
  const filteredMatters = useMemo(() => {
    if (!filterMatters) {
      return matters;
    }
    return matters.filter((matter) => filterMatters(matter));
  }, [matters, filterMatters]);

  if (matters.length > 0 && !matters[0]?.id) {
    return (
      <Card className="w-full">
        <CardHeader className="space-y-1">
          <Typography variant="subHeading" className="text-muted-foreground">
            You do not have permission to view matters.
          </Typography>
        </CardHeader>
      </Card>
    );
  }

  if (chrome === "matterDashboard") {
    return (
      <DraggableCardDataTable
        searchBar={searchBar}
        tableToolbarBeforeColumns={tableToolbarBeforeColumns}
        tableButtons={tableButtons}
        columns={mattersTableColumnsPlainHeaders}
        data={filteredMatters}
        isLoading={isMattersLoading}
        isFetching={isMattersFetching}
        isError={isMattersError}
        onRowClick={onRowClick}
        renderContextMenu={
          renderContextMenu ??
          ((row, trigger) => (
            <MatterRowContextMenu
              allowedStatusCategories={statusCategories}
              matter={row.original}
              trigger={trigger}
            />
          ))
        }
        tableName={tableName}
        entityLabelPlural={entityLabelPlural}
        fixedColumnWidths
        initialSorting={[{ id: "Date Created", desc: true }]}
        initialVisibility={{
          "Date Created": false,
          "Date Modified": false,
        }}
        serverPagination={{
          pagination,
          onPaginationChange: setPagination,
          pageCount,
          rowCount: stableRowCount,
          getCanNextPage: Boolean(nextPageNumber),
        }}
        columnIdAliases={INCIDENT_TYPE_COLUMN_ALIASES}
        newColumnAnchors={newColumnAnchors}
        serverSorting={{ sorting, onSortingChange: setSorting }}
        scrollableBody={scrollableBody}
      />
    );
  }

  return (
    <DataTable
      chrome={chrome}
      entityLabelPlural={entityLabelPlural}
      hideSearchBar
      leftToolbarContent={searchBar}
      toolbarFilters={toolbarFilters}
      tableButtons={tableButtons}
      columns={mattersTableColumns}
      initialSort={{ id: "Date Created", desc: true }}
      data={filteredMatters}
      onRowClick={(row) => {
        onRowClick(row);
      }}
      isLoading={isMattersLoading}
      isFetching={isMattersFetching}
      isError={isMattersError}
      tableName={tableName}
      initialVisibility={{
        "Date Created": false,
        "Date Modified": false,
      }}
      serverPagination={{
        pagination,
        onPaginationChange: setPagination,
        pageCount,
        rowCount: stableRowCount,
        getCanNextPage: Boolean(nextPageNumber),
      }}
      columnIdAliases={INCIDENT_TYPE_COLUMN_ALIASES}
      newColumnAnchors={newColumnAnchors}
      serverSorting={{ sorting, onSortingChange: setSorting }}
    />
  );
};
